import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { coreSelectors } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import Conversation from 'entity/Conversation';
import User from 'entity/User';
import { SocialState } from 'messages/websocket.messages';
import { SocialEventType, Convo, ConvoPhase } from 'messages/websocket.messages';

import ModalActionType from '../modal-social-event/ModalActionType';
import SocialStateHelper from '../social-state-machine/social-state-helper';
import SocialStateMachine from '../social-state-machine/social-state-machine';
import { mediaSelectors } from '../state/media';

import { ModalSocialEventService } from './modal-social-event.service';
import { MultimediaRoomService } from './multimedia-room.service';
import { MultimediaUserService } from './multimedia-user.service';
import { SceneService } from './scene.service';
import { ToasterVideoService } from './toaster-video.service';

@Injectable()
export class ConvoService extends Loggable implements OnDestroy {
  public isStarting = false;
  public isInProgress = false;
  private conversationInProgress?: Conversation;
  public user?: User;
  public isPresenter = false;
  private isCameraDisabled = false;
  private isVideoEnabled = true;
  private isCameraReadable = false;

  private socialStateSubscription: Subscription;
  private destroyed$ = new ReplaySubject<boolean>(1);

  public constructor(
    private authService: AuthService,
    private modalSocialEventService: ModalSocialEventService,
    private sceneService: SceneService,
    private toasterVideoService: ToasterVideoService,
    private multimediaUserService: MultimediaUserService,
    private multimediaRoomService: MultimediaRoomService,
    store: Store<AppState>,
  ) {
    super();

    store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
      });

    // Handle Media state
    store
      .select(mediaSelectors.isCameraDisabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCameraDisabled) => (this.isCameraDisabled = isCameraDisabled));

    store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => (this.isVideoEnabled = enabled));

    store
      .select(mediaSelectors.isCurrentCameraReadable)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((readable) => {
        this.isCameraReadable = readable;
      });

    this.socialStateSubscription = SocialStateMachine.shared.stateChanged$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((res) => {
        if (!res) {
          return;
        }

        // End any convo logic in progress if the state tells us there's no convo anymore
        if (!this.isConvoHappeningCheck(res)) {
          // Check to make sure the service doesn't think an event is in progress that isn't, and update accordingly.
          if (this.isStarting || this.isInProgress || this.conversationInProgress) {
            this.handleConvoEnded();
          }
          return;
        }

        const convo = res.socialEvent as Convo;
        const userIsPresenter = this.user?.userId === convo.presenter.userId;
        this.isPresenter = userIsPresenter;

        // Non-presenter logic is handled in convo-participant.service.ts!
        if (!userIsPresenter) {
          return;
        }
        if (convo.phase === ConvoPhase.ANNOUNCE_PHASE && !this.isStarting) {
          void this.start(convo);
        } else if (convo.phase === ConvoPhase.BANNER_PHASE && !this.isInProgress) {
          this.runConvo();
        }
      });
  }

  private isConvoHappeningCheck(providedState?: SocialState, failLogText?: string): boolean {
    const state = providedState || SocialStateMachine.shared.state;
    return !!state && SocialStateHelper.isEventHappening(state, SocialEventType.CONVO, failLogText);
  }

  /**
   *  Pre Requisites:
   *    Validate if there is no other Social Event initiated
   * Stages
   *   - convoStart
   *   - convoInProgress
   *   - convoEnd
   *
   * 1. ConvoStart
   *   1.1 Player: Modal - Your Convo is about to Begin
   *   1.2 Everyone: Show Sticky Video
   *   1.3 Player: Prompt Convo button triggers ConvoInProgress*
   */
  private async start(convo: Convo): Promise<void> {
    const { conversation } = convo;
    // Set Flags
    this.isStarting = true;
    this.isInProgress = false;
    this.conversationInProgress = convo.conversation;

    // Set Music Volume while EventOn and update Participants Volume
    const player = this.sceneService?.sceneManager?.player;
    if (!player) {
      return this.error('Ice Breaker start attempted without player');
    }

    this.multimediaRoomService.adjustSoundForToastStart();

    if (!this.isCameraDisabled && !this.isVideoEnabled && this.isCameraReadable) {
      this.multimediaUserService.toggleVideo();
    }

    // Add the Host/Player Video to the right corner of the screen
    await this.toasterVideoService.addToaster(player.userId);

    // 1.1 Player: Modal - Your Convo is about to Begin
    this.modalSocialEventService
      .open({
        title: 'Your Ice Breaker is About to Begin.',
        message: 'Introduce the topic and give any necessary instructions (i.e. "discuss in groups of 3")',
        messageBold: conversation.title ?? '',
        cancelButtonTitle: 'Cancel',
        confirmButtonTitle: 'Start',
        showContent: true,
        isPresenter: true,
      })
      .subscribe({
        next: async (val) => {
          if (val === ModalActionType.PRIMARY) {
            // 2. ConvoInProgress
            await this.requestMoveToBannerPhase();
          } else {
            await this.requestEndConvo();
          }
        },
      });
  }

  public async requestStartConvo(conversation: Conversation) {
    const userId = this.user?.userId;
    if (!this.user || !userId) {
      return this.error('Requested start ice breaker without a user');
    }
    const isGuest = this.authService.isAnonymous();
    await SocialStateMachine.shared
      .requestStartNewConvo({
        presenter: { ...this.user, userId, isGuest },
        userId: this.user?.userId || '', // this would be worthy of a non-null assertion
        convoMessage: conversation.title ?? '',
        conversation: conversation,
      })
      .catch((e) => this.error('Error request start convo', e));
  }

  private async requestMoveToBannerPhase() {
    await SocialStateMachine.shared.requestConvoToBannerPhase({ userId: this.user?.userId || '' });
  }

  /**
   * 2. ConvoInProgress
   *   2.1 Set Flags
   *   2.2 Host: Remove Host from Video Podium
   *   2.3 Broadcast Socket Message convo_in_progress
   *     - Non-Host: Remove Host from Video Podium
   *     - Non-Host: See simple Convo prompt
   *   2.4 Host: See prompt with END Button
   */
  private runConvo(): void {
    //  2.1 Set Flags
    this.isStarting = false;
    this.isInProgress = true;

    // Remove Toaster
    this.toasterVideoService.removeToaster();

    // Restore Participants Volume
    this.multimediaRoomService.resetSoundForToastEnd();

    // 2.4 Host: See prompt with END Button
    this.modalSocialEventService
      .open({
        title: 'YOU ARE HOSTING AN ICE BREAKER',
        messageBold: this.conversationInProgress?.title || '',
        confirmButtonTitle: 'END ICE BREAKER',
        showContent: true,
        isPresenter: true,
      })
      .subscribe({
        next: async () => {
          // Is the Convo Host? Yes because we are in the modal displayed to the Convo Host
          await this.requestEndConvo();
        },
      });
  }

  /**
   * 3. ConvoEnd
   *   - Set Flags
   *   - Broadcast Socket Message convo_end
   *   - Make sure we remove Host Video from Right Corner
   *     - Non-Host: Remove Host Video from Right Corner
   */
  private handleConvoEnded(): void {
    this.modalSocialEventService.close();

    //  Set Flags
    this.isStarting = false;
    this.isInProgress = false;
    this.conversationInProgress = undefined;

    // Remove toaster
    this.toasterVideoService.removeToaster();

    // Restore Participants Volume
    this.multimediaRoomService.resetSoundForToastEnd();
  }

  public async requestEndConvo() {
    await SocialStateMachine.shared
      .requestEndEvent({
        prevEventType: SocialEventType.CONVO,
        userId: this.user?.userId || '',
      })
      .catch((e) => this.error('Error request end convo', e));
    this.handleConvoEnded();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    if (this.socialStateSubscription) {
      this.socialStateSubscription.unsubscribe();
    }
  }
}
